import { useQuery } from '@tanstack/react-query';
import { addDoc, getDocs, limit, query, serverTimestamp, Timestamp, where } from 'firebase/firestore';

import { useAuth } from '@/app/auth';
import type { Home, HomeDoc } from '@/features/homeAutomation/home';
import { homesCollection } from '@/lib/firestorePaths';

const STALE_TIME_MS = 5 * 60_000;

async function fetchOrCreateCurrentHome(uid: string): Promise<Home> {
  const q = query(homesCollection(), where('ownerUid', '==', uid), limit(1));
  const snapshot = await getDocs(q);
  if (!snapshot.empty) {
    const docSnap = snapshot.docs[0]!;
    return { id: docSnap.id, ...docSnap.data() };
  }
  const pending = Timestamp.now();
  const newDoc: HomeDoc = {
    name: 'Home',
    ownerUid: uid,
    // Writes use serverTimestamp; the placeholder Timestamps here are only
    // returned to the caller until the next refetch reconciles real values.
    createdAt: pending,
    updatedAt: pending,
  };
  const ref = await addDoc(homesCollection(), {
    ...newDoc,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  } as HomeDoc);
  return { id: ref.id, ...newDoc };
}

export default function useCurrentHome() {
  const { status, user } = useAuth();
  const uid = user?.uid ?? '';
  const enabled = status === 'authorized' && !!uid;

  return useQuery({
    queryKey: ['homeAutomation', 'currentHome', uid],
    queryFn: () => fetchOrCreateCurrentHome(uid),
    enabled,
    staleTime: STALE_TIME_MS,
  });
}
